import { type AnyAction } from 'redux';
import actions from './actions';
import { InitializationSteps } from '@/constants/basket';
import paymentActions from '@/redux/payment/actions';
import {
  type BasketPricing,
  type IOrderStatusState,
  OrderStatuses,
  OrderStatusTypes,
  SubmitFailureCause,
} from '@/types/orderStatus';
import { type RootState } from '@/types/app';
import { isToastRecoveryModalCategory } from '@/utils/toastErrorClassifier';

export const initialState: IOrderStatusState = {
  type: OrderStatusTypes.OK,
  message: null,
  messageAction: null,
  initializationErrorType: null,
  status: OrderStatuses.ORDER_NOT_INITIALIZED,
  recovery502Endpoint: null,
  toastRecoveryCategory: null,
  toastConsecutiveFailures: 0,
  cause: null,
  submitFailed: false,
  preAuthValidation: null,
  basketPricing: null,
};

// TEAM-14645: recovery transitions clear a terminal cause so the Place Order button
// re-enables once the diner has fixed what failed. A card decline passes the validate step,
// so a PAYMENT_DECLINED cause must survive a re-validate/orderPending and only clear when the
// diner changes their payment method (see the payment-action cases below).
const clearNonPaymentCause = (cause?: SubmitFailureCause | null) =>
  cause === SubmitFailureCause.PAYMENT_DECLINED ? cause : null;

/**
 * TEAM-14493: the transient / pending transitions PRICING_UNAVAILABLE refuses. That state is
 * terminal — its blocking modal offers no retry — and Place Order is gated on
 * `status === ORDER_PENDING`, so it only stays terminal if nothing incidental can move `status` off
 * it. Each action below is fired by something the diner did not choose while looking at the modal,
 * and each would otherwise close it and re-arm Place Order:
 *
 * - `<Rewards />` mounts once the failed checkout load hydrates `checkoutBasket`, and
 *   `getRewardsSaga` puts BASKET_REWARDS_GET, then ORDER_PENDING in its `finally` (or
 *   RECOVERY_502_TRIGGERED on a 502).
 * - `<Tip />` auto-applies a default tip once `type` leaves UPDATING: TIP_ADD, then `tipAddSaga`'s
 *   ORDER_PENDING on success and in its catch.
 * - ORDER_SUBMISSION_PCI_ERROR — the PCI-proxy secure-fields `error` event
 *   (`payment/pci-proxy/form/hooks.ts`) fires without a press, e.g. when the iframe fails to
 *   initialize, and this case sets `status: ORDER_PENDING`.
 * - Every other basket-write status below, and the ORDER_PENDING each of their sagas settles on.
 *
 * Their sagas still run — this only keeps the reducer from leaving PRICING_UNAVAILABLE for them.
 *
 * TEAM-16128: RESTAURANT_UNAVAILABLE is now guarded by the same set — its modal is terminal for the
 * same reason, and the same incidental actions would close it.
 *
 * Deliberately NOT here, so these still leave PRICING_UNAVAILABLE:
 * - INITIALIZE_ORDER — the re-entry path. The cart's Checkout CTA re-initializes from
 *   PRICING_UNAVAILABLE when there is no `checkoutBasket.id` (`components/cart/footer.tsx`): a
 *   code 13 at an init step before VALIDATE (e.g. SET_WANTED_AT, whose coo-api call runs the
 *   chowly-api validate) never hydrates `checkoutBasket`, so the checkout page's mount-time
 *   validate has nothing to validate. The new init's outcome decides: success settles on
 *   ORDER_PENDING (by then INITIALIZING_ORDER, so not refused); code 13 lands straight back here.
 * - VALIDATE_ORDER / PRE_AUTH_VALIDATE — a fresh validate of the basket: the checkout page's
 *   mount-time validate, a cart edit synced to the checkout basket, a sign-in re-validate. Same
 *   outcome rule.
 * - INITIALIZE_ORDER_RECONCILIATION, ORDER_NOT_INITIALIZED, DESTROY_ORDER — a teardown.
 * - ORDER_TIME_UNAVAILABLE (AC3: closed-by-hours outranks it), and DELIVERY_UNAVAILABLE — another
 *   blocking end state. RESTAURANT_UNAVAILABLE is now guarded (TEAM-16128).
 * - The rest of the submission sequence (triggers, start, submission error / page error). Each is
 *   reached only from a Place Order press or a payment sheet the diner confirmed — both unreachable
 *   behind the modal — and their sagas cannot be stopped from here.
 * - `forceOrderPending()` — the explicit-dismissal override, as for ORDER_TIME_UNAVAILABLE and the
 *   502 modal. No surface can dispatch it while the modal is open.
 * - The message actions, which do not touch `status`; the pricing modal never reads `message`.
 */
const REFUSED_WHILE_PRICING_UNAVAILABLE: ReadonlySet<string> = new Set([
  actions.ORDER_PENDING,
  actions.INITIALIZE_ORDER_RESUME,
  actions.PROMO_CODE_ADD,
  actions.PROMO_CODE_REMOVE,
  actions.CONVEYANCE_SET,
  actions.CONVEYANCE_DELIVERY_SETTING,
  actions.CONVEYANCE_DELIVERY_SET,
  actions.WANTED_AT_BASKET_SET,
  actions.WANTED_AT_BASKET_REMOVE,
  actions.BASKET_REWARDS_GET,
  actions.BASKET_REWARDS_ADD,
  actions.BASKET_REWARDS_REMOVE,
  actions.TIP_ADD,
  actions.UTENSILS_SET,
  actions.BASKET_INSTRUCTIONS_SET,
  actions.STORED_VALUE_POST,
  actions.PAYMENT_APPLYING,
  actions.RECOVERY_502_TRIGGERED,
  actions.ORDER_SUBMISSION_PCI_ERROR,
]);

/**
 * TEAM-15690: what BASKET_NOT_FOUND refuses — the same incidental transitions as
 * PRICING_UNAVAILABLE (above), for the same reason, plus VALIDATE_ORDER.
 *
 * VALIDATE_ORDER is an exit from PRICING_UNAVAILABLE because a fresh validate there can succeed.
 * Here it cannot: coo-api has no basket under this id, so the validate 404s again. Left open, the
 * checkout page's mount-time validate (`<OrderStatus />`) and the gift-card tip refresh would each
 * move the order to VALIDATING_ORDER for the length of the request — taking the notice down and
 * putting the "Order updating...." label on Place Order — before the 404 put it back. Their sagas
 * still run: `surfaceBasketNotFound` does not report a second 404 for the same basket, and the
 * BASKET_NOT_FOUND it still dispatches leaves this state as it is (see the case below).
 *
 * Still exits, so the diner is never stuck: DESTROY_ORDER ("Start a new cart"), INITIALIZE_ORDER,
 * ORDER_NOT_INITIALIZED, PRE_AUTH_VALIDATE, the submission sequence and `forceOrderPending()`, as for
 * PRICING_UNAVAILABLE.
 */
const REFUSED_WHILE_BASKET_NOT_FOUND: ReadonlySet<string> = new Set([
  ...REFUSED_WHILE_PRICING_UNAVAILABLE,
  actions.VALIDATE_ORDER,
]);

/** The terminal states that refuse incidental transitions, each with its own set. */
const REFUSED_WHILE_TERMINAL: Partial<Record<OrderStatuses, ReadonlySet<string>>> = {
  [OrderStatuses.PRICING_UNAVAILABLE]: REFUSED_WHILE_PRICING_UNAVAILABLE,
  [OrderStatuses.RESTAURANT_UNAVAILABLE]: REFUSED_WHILE_PRICING_UNAVAILABLE,
  [OrderStatuses.BASKET_NOT_FOUND]: REFUSED_WHILE_BASKET_NOT_FOUND,
};

const isRefusedInTerminalState = (state: IOrderStatusState, action: AnyAction) =>
  Boolean(REFUSED_WHILE_TERMINAL[state.status]?.has(action.type)) &&
  !(action.type === actions.ORDER_PENDING && action.force);

export const orderStatus = (state = initialState, action: AnyAction) => {
  if (isRefusedInTerminalState(state, action)) {
    return state;
  }

  switch (action.type) {
    // Message Actions
    case actions.ORDER_STATUS_MESSAGE_CLEAR:
      return Object.assign({}, state, {
        type: OrderStatusTypes.OK,
        message: initialState.message,
        messageAction: null,
        initializationErrorType: initialState.initializationErrorType,
      });
    case actions.ORDER_STATUS_MESSAGE_ERROR_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        message: action.message,
        messageAction: action.messageAction ?? null,
        initializationErrorType: null,
      });

    // Generic Actions
    case actions.ORDER_PENDING:
      if (state.status === OrderStatuses.ORDER_TIME_UNAVAILABLE && !action.force) {
        return state;
      }
      // Preserve an active 502 recovery state across post-error orderPending dispatches
      // (e.g. rewards sagas always put ORDER_PENDING in their `finally`). The modal
      // dismisses the recovery state explicitly.
      if (state.type === OrderStatusTypes.RECOVERY_502 && !action.force) {
        return state;
      }
      // Same preservation for Toast payment recovery — the modal dismisses explicitly
      if (state.type === OrderStatusTypes.TOAST_PAYMENT_RECOVERY && !action.force) {
        return state;
      }
      return Object.assign({}, state, {
        type: OrderStatusTypes.OK,
        message: null,
        initializationErrorType: null,
        status: OrderStatuses.ORDER_PENDING,
        recovery502Endpoint: null,
        toastRecoveryCategory: null,
        cause: clearNonPaymentCause(state.cause),
      });
    case actions.ORDER_NOT_INITIALIZED:
      // TEAM-14787 (AC3): the pricing verdict survives. This is what dismissing the
      // reconciliation modal dispatches, and on a basket that already exists that leaves the
      // summary's error block as the only way back in; resetting the verdict here stranded the
      // diner on a spinner with no retry. INITIALIZE_ORDER still clears it.
      return Object.assign({}, initialState, { basketPricing: state.basketPricing });

    // Initialization Sequence
    case actions.INITIALIZE_ORDER:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        initializationErrorType: null,
        status: OrderStatuses.INITIALIZING_ORDER,
        utm_source: action.basketOrder?.utm_source,
        toastConsecutiveFailures: 0,
        // TEAM-14644: a new checkout starts with a clean slate. Without this the flag
        // survives from a previous basket's failed submit (this case spreads the old
        // state, and only DESTROY_ORDER / ORDER_NOT_INITIALIZED reset it), so the very
        // FIRST press on the new basket would re-validate -- breaking the invariant
        // that the first attempt is unchanged (review finding 2).
        //
        // Reachable from the error-reconciliation handlers, which dispatch
        // INITIALIZE_ORDER directly after a checkout error with no status guard
        // (errorReconciliation/index.tsx, PickupTimeErrorHandler.tsx). The cart footer's
        // checkout CTA is NOT a route in from a failed submit: it only initializes from
        // ORDER_NOT_INITIALIZED or a basket-less PRICING_UNAVAILABLE (TEAM-14493), and a failed
        // submit leaves it ORDER_PENDING.
        submitFailed: false,
        preAuthValidation: null,
        // TEAM-14645: clear the submit-failure cause on a fresh checkout too. Without this a
        // terminal PAYMENT_DECLINED / DELIVERY_QUOTE leaks in from a previous basket (this case
        // spreads the old state) and gates Place Order from the start — permanent on a
        // gift-card-covered / zero-total basket, which has no card-entry surface to clear it.
        cause: null,
        // TEAM-14787: a (re-)initialization has to prove its price again. Keeping the previous
        // verdict would show a restarted checkout's old figures if this run's validate fails.
        basketPricing: null,
      });
    case actions.INITIALIZE_ORDER_ERROR:
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        message: action.message,
        status: OrderStatuses.ORDER_NOT_INITIALIZED,
      });
    case actions.INITIALIZE_ORDER_RECONCILIATION:
      return Object.assign({}, state, {
        type: OrderStatusTypes.RECONCILIATION,
        message: action.message,
        initializationErrorType: action.initializationErrorType ?? null,
        // TEAM-15138: written unconditionally, so a later failure of a different kind clears what a
        // required-option failure left behind rather than leaving the modal pointing at a cart line
        // the current error has nothing to do with.
        requiredOption: action.requiredOption ?? null,
        status: OrderStatuses.ORDER_NOT_INITIALIZED,
      });
    case actions.INITIALIZE_ORDER_RESUME:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.INITIALIZING_ORDER,
      });

    // Validate Order
    case actions.VALIDATE_ORDER:
      // Preserve Toast payment recovery state across basket revalidation while
      // the recovery modal shows — so not for WALLET_ESCALATE, which has no
      // modal overlay. Allowing UPDATING through keeps intentUpdatePending true
      // so the submit button stays disabled while the basket is mid-update.
      if (
        state.type === OrderStatusTypes.TOAST_PAYMENT_RECOVERY &&
        isToastRecoveryModalCategory(state.toastRecoveryCategory)
      ) {
        return state;
      }
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.VALIDATING_ORDER,
        // A re-validate is how a diner retries after changing their address; clear a
        // non-payment terminal cause so the button can re-enable if the validate succeeds.
        cause: clearNonPaymentCause(state.cause),
      });
    // TEAM-14644: same status transition as VALIDATE_ORDER on purpose — the basket is being
    // written, so every basket-mutation control (tip, promo, instructions, cutlery) must be
    // held exactly as it is for any other validate. The extra `preAuthValidation` is the
    // part the caller needs: it says whether authorizing is allowed once this settles.
    case actions.PRE_AUTH_VALIDATE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.VALIDATING_ORDER,
        preAuthValidation: 'pending',
      });
    case actions.PRE_AUTH_VALIDATE_SETTLED:
      // Only a validation that is still in flight can publish a verdict. Anything that
      // cleared `preAuthValidation` while this one was running (a new checkout, a fresh
      // failure, the submit it was gating) has superseded it, and re-arming a verdict on top
      // of that would hand a caller an authorization token for a press that is over.
      if (state.preAuthValidation !== 'pending') {
        return state;
      }
      return Object.assign({}, state, {
        preAuthValidation: action.verdict,
      });

    // Promo Code
    case actions.PROMO_CODE_ADD:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.PROMO_CODE_ADDING,
      });
    case actions.PROMO_CODE_REMOVE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.PROMO_CODE_REMOVING,
      });

    // Set Conveyance
    case actions.CONVEYANCE_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.CONVEYANCE_SETTING,
      });
    case actions.CONVEYANCE_DELIVERY_SETTING:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.CONVEYANCE_DELIVERY_SETTING,
      });
    case actions.CONVEYANCE_DELIVERY_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.CONVEYANCE_DELIVERY_SETTING,
      });

    // Set Wanted At
    case actions.WANTED_AT_BASKET_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.WANTED_AT_BASKET_SETTING,
      });
    case actions.WANTED_AT_BASKET_REMOVE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.WANTED_AT_BASKET_REMOVING,
      });

    case actions.ORDER_TIME_UNAVAILABLE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.WARNING,
        status: OrderStatuses.ORDER_TIME_UNAVAILABLE,
        message: action.message,
        initializationErrorType: null,
      });

    // Basket Rewards
    case actions.BASKET_REWARDS_GET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.BASKET_REWARDS_GETTING,
      });
    case actions.BASKET_REWARDS_ADD:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.BASKET_REWARDS_ADDING,
      });
    case actions.BASKET_REWARDS_REMOVE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.BASKET_REWARDS_REMOVING,
      });

    // Tip
    case actions.TIP_ADD:
      if (state.status === OrderStatuses.ORDER_TIME_UNAVAILABLE) {
        return state;
      }
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.TIP_ADDING,
      });
    // End Tip

    case actions.UTENSILS_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.UTENSILS_SETTING,
      });

    // Basket Instructions
    case actions.BASKET_INSTRUCTIONS_SET:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.BASKET_INSTRUCTIONS_SETTING,
      });

    // Stored Value
    case actions.STORED_VALUE_POST:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        message: null,
        status: OrderStatuses.STORED_VALUE_POSTING,
      });

    case actions.PAYMENT_APPLYING:
      return Object.assign({}, state, {
        type: OrderStatusTypes.UPDATING,
        status: OrderStatuses.PAYMENT_APPLYING,
        message: null,
      });

    // Submission Sequence
    case actions.DIGITAL_WALLET_ORDER_STARTED:
      return Object.assign({}, state, {
        type: OrderStatusTypes.TRANSMITTING,
        status: OrderStatuses.DIGITAL_WALLET_ORDER_STARTED,
        message: null,
      });
    case actions.ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER:
      return Object.assign({}, state, {
        type: OrderStatusTypes.TRANSMITTING,
        status: OrderStatuses.ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGERED,
        message: null,
      });
    case actions.ORDER_SUBMISSION_TRIGGER:
      return Object.assign({}, state, {
        type: OrderStatusTypes.TRANSMITTING,
        message: null,
        captcha: action.captcha,
        status: OrderStatuses.ORDER_SUBMISSION_TRIGGERED,
        // TEAM-14644: consume the pre-authorization verdict as the submit it gated starts.
        // Leaving 'succeeded' behind would let a LATER press (after another failure) read a
        // verdict from the previous attempt and authorize without re-validating.
        preAuthValidation: null,
        // TEAM-12423: reset the Toast failure counter when a submit actually goes through.
        // Without this a transient-retry success (or any earlier failure followed by a
        // successful submit) leaves the counter at 1+, and a later genuinely-first failure
        // on the same basket lands on the multi-failure modal instead of its own category's.
        toastConsecutiveFailures: 0,
      });
    // Note (TEAM-14644): the PCI cases intentionally do NOT set `submitFailed`. This is a
    // card-tokenization error from the PCI-proxy form, not a basket-submit rejection, so
    // re-validating the basket on retry would not address it — it is outside this ticket's
    // "same rejected basket" population.
    case actions.ORDER_SUBMISSION_PCI_ERROR:
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.ORDER_PENDING,
        message: action.message,
        messageAction: null,
      });
    case actions.ORDER_SUBMISSION_PCI_FAILURE:
      // `orderSubmissionPciFailure()` takes no argument, so `action.message` was always
      // `undefined` — a type lie against `message: string | null`. The PCI-proxy iframe surfaces its
      // own copy; this action only moves the status.
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.ORDER_SUBMISSION_PCI_FAILURE,
        message: null,
        messageAction: null,
      });
    case actions.ORDER_SUBMISSION_ERROR:
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.ORDER_PENDING,
        message: action.message,
        messageAction: action.messageAction ?? null,
        // TEAM-14645: carry the parsed cause so the Place Order button can gate on it.
        cause: action.cause ?? null,
        // TEAM-14644: mark the basket as having a failed submit so the next Place
        // Order press re-validates before resubmitting instead of re-sending the
        // same rejected payload.
        submitFailed: true,
        // ...and drop any pre-authorization verdict. A fresh failure invalidates it, and
        // leaving it set would let a component that remounts afterwards read a verdict
        // from the previous attempt and act on it again.
        preAuthValidation: null,
        // TEAM-14291: whether a Toast payment was authorized before this failure.
        toastPaymentWasAuthorized: action.toastPaymentWasAuthorized ?? false,
      });
    case actions.ORDER_SUBMISSION_PAGE_ERROR:
      return Object.assign({}, state, {
        type: OrderStatusTypes.PAGE_ERROR,
        status: OrderStatuses.ORDER_PENDING,
        message: action.message,
        messageAction: null,
        cause: action.cause ?? null,
        submitFailed: true,
        preAuthValidation: null,
        toastPaymentWasAuthorized: action.toastPaymentWasAuthorized ?? false,
      });
    case actions.RECOVERY_502_TRIGGERED:
      return Object.assign({}, state, {
        type: OrderStatusTypes.RECOVERY_502,
        status: OrderStatuses.ORDER_PENDING,
        message: null,
        recovery502Endpoint: action.endpoint,
        toastPaymentWasAuthorized: action.toastPaymentWasAuthorized ?? false,
      });

    case actions.TOAST_PAYMENT_RECOVERY:
      // ORDER_PENDING here means "ready for another attempt", not "order is in flight".
      // The type (TOAST_PAYMENT_RECOVERY) is what distinguishes it — same pattern as RECOVERY_502.
      return Object.assign({}, state, {
        type: OrderStatusTypes.TOAST_PAYMENT_RECOVERY,
        status: OrderStatuses.ORDER_PENDING,
        // TEAM-15276: the recovery modal owns all of this failure's copy (same rule as
        // RECOVERY_502 / PRICING_UNAVAILABLE), so no status-bar message alongside it — the
        // decline copy contradicted the modal ("didn't go through" next to "may have gone
        // through"). WALLET_ESCALATE has no modal and keeps its message.
        message: isToastRecoveryModalCategory(action.category) ? null : action.message,
        toastRecoveryCategory: action.category,
        toastConsecutiveFailures: (state.toastConsecutiveFailures ?? 0) + 1,
        // TEAM-14644: a Toast confirm-payment failure is a post-submit rejection,
        // so the next Place Order press must re-validate the basket (pre-auth)
        // before resubmitting — same as ORDER_SUBMISSION_ERROR.
        submitFailed: true,
        preAuthValidation: null,
      });

    case actions.DELIVERY_UNAVAILABLE:
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.DELIVERY_UNAVAILABLE,
        message: null,
        cause: null,
      });

    case actions.RESTAURANT_UNAVAILABLE:
      // AC3 state precedence (TEAM-14911): a closed-by-hours cause outranks a paused / POS-unavailable
      // one, so don't overwrite an active ORDER_TIME_UNAVAILABLE state (mirrors ORDER_PENDING and
      // TIP_ADD above). Centralizing it here covers all three dispatch sites — init, validate, submit.
      // Runs before the state build below, so the reason TEAM-14642 carries is not applied either:
      // the diner keeps the higher-priority closed-by-hours state, message included.
      if (state.status === OrderStatuses.ORDER_TIME_UNAVAILABLE) {
        return state;
      }

      // TEAM-14642: keep the reason the service supplied instead of discarding it.
      // This case used to hardcode `message: null`, so correcting detection on its
      // own would have traded the specific reason the diner can currently see for
      // generic copy with none. `?? null` preserves the old shape when no reason
      // was passed.
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.RESTAURANT_UNAVAILABLE,
        message: action.message ?? null,
        // TEAM-14645: this is not a submit-failure cause, so the gate is cleared here.
        cause: null,
        // TEAM-14291: carry the flag so hold-release messaging is available.
        toastPaymentWasAuthorized: action.toastPaymentWasAuthorized ?? false,
      });

    case actions.PRICING_UNAVAILABLE:
      // TEAM-14493: same AC3 precedence as RESTAURANT_UNAVAILABLE above — an active
      // closed-by-hours state outranks this one and is left in place.
      if (state.status === OrderStatuses.ORDER_TIME_UNAVAILABLE) {
        return state;
      }

      // Terminal: `message` is cleared so neither the status bar nor any other surface renders a
      // sentence beside the blocking modal, which owns all of this failure's copy.
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.PRICING_UNAVAILABLE,
        message: null,
        messageAction: null,
        recovery502Endpoint: null,
        cause: null,
      });

    case actions.BASKET_NOT_FOUND:
      // TEAM-15690: the same basket answering 404 again changes nothing — see `lostBasketId`.
      if (
        state.status === OrderStatuses.BASKET_NOT_FOUND &&
        state.lostBasketId === action.basketId
      ) {
        return state;
      }

      // Terminal, and unlike RESTAURANT_UNAVAILABLE / PRICING_UNAVAILABLE it lands over an active
      // closed-by-hours state too: with no basket on coo-api's side, nothing that state offers (a
      // new time) can act on this one.
      // `message` is cleared so no other surface renders a sentence beside the notice, which owns
      // this failure's copy. `preAuthValidation` is left alone, as PRICING_UNAVAILABLE leaves it:
      // a pre-auth validate that 404s still publishes its 'failed' verdict after this.
      return Object.assign({}, state, {
        type: OrderStatusTypes.ERROR,
        status: OrderStatuses.BASKET_NOT_FOUND,
        message: null,
        messageAction: null,
        initializationErrorType: null,
        requiredOption: null,
        recovery502Endpoint: null,
        toastRecoveryCategory: null,
        cause: null,
        lostBasketId: action.basketId,
        toastPaymentWasAuthorized: action.toastPaymentWasAuthorized ?? false,
      });

    // TEAM-14645: the diner picking/entering a payment method is what clears a
    // PAYMENT_DECLINED gate — "not offered again against the same card". Deliberate
    // selection actions only; DIGITAL_WALLET_TOGGLE and PAYMENT_TYPE_RESET are excluded
    // because they also fire automatically on a submit failure and would clear the gate
    // the instant it is set.
    case paymentActions.CREDIT_CARD_SET:
    case paymentActions.BILLING_ACCOUNT_SET:
    case paymentActions.PAY_IN_STORE_SET:
    case paymentActions.TOAST_PAYMENT_CREATE:
      return state.cause === SubmitFailureCause.PAYMENT_DECLINED
        ? Object.assign({}, state, { cause: null })
        : state;

    // TEAM-14787: 'priced' is sticky for its basket id — see `IOrderStatusState.basketPricing`.
    case actions.BASKET_PRICING_SETTLED:
      if (
        state.basketPricing?.basketId === action.basketId &&
        state.basketPricing?.outcome === 'priced'
      ) {
        return state;
      }
      return Object.assign({}, state, {
        basketPricing: {
          basketId: action.basketId,
          outcome: action.outcome,
          // A later checkout-page validate failing on the same basket does not know which setup
          // step failed; keep the step so the summary's retry still re-runs initialization.
          failedStep:
            action.outcome === 'priced'
              ? null
              : action.failedStep ??
                (state.basketPricing?.basketId === action.basketId
                  ? state.basketPricing?.failedStep ?? null
                  : null),
        },
      });

    case actions.DESTROY_ORDER:
      return initialState;
    default:
      return state;
  }
};

/** TEAM-15690: coo-api has lost the checkout basket — the inline "Start a new cart" notice is up. */
export const selectIsBasketNotFound = (state: RootState): boolean =>
  state.app.orderStatus.status === OrderStatuses.BASKET_NOT_FOUND;

export const isConveyanceInFlight = (state: RootState) =>
  state.app.conveyanceMode.loading ||
  (state.app.orderStatus.type === OrderStatusTypes.UPDATING &&
    (state.app.orderStatus.status === OrderStatuses.CONVEYANCE_SETTING ||
      state.app.orderStatus.status === OrderStatuses.CONVEYANCE_DELIVERY_SETTING));

// Whether a basket-writing request is currently in flight (TEAM-14696).
//
// Only `type` is authoritative. An earlier version also OR-ed a
// `BASKET_MUTATING_STATUSES.includes(status)` list, which turned every stale
// status (e.g. an error path that dispatches ORDER_STATUS_MESSAGE_ERROR_SET,
// which preserves `status`) into a permanent guard latch. Now: when the type
// flips to ERROR / WARNING / OK / etc., mutations are considered done, and
// `basketMutationGuard` bounds any remaining "type never leaves UPDATING"
// case with a stuck-mutation escape hatch.
export const isBasketMutating = (state: RootState): boolean => {
  const { type } = state.app.orderStatus;
  return type === OrderStatusTypes.UPDATING || type === OrderStatusTypes.TRANSMITTING;
};

/** TEAM-14787: the pricing verdict for the CURRENT checkout basket, or null if none settled. */
const selectCheckoutBasketPricingOutcome = (state: RootState): BasketPricing['outcome'] | null => {
  const basketId = state.app.basket.checkoutBasket?.id;
  const pricing = state.app.orderStatus.basketPricing;
  return basketId && pricing?.basketId === basketId ? pricing.outcome : null;
};

/**
 * TEAM-14787: whether the current checkout basket has been priced by an app-side validate.
 * `total === 0` is not a substitute — see `IOrderStatusState.basketPricing`.
 */
export const selectIsCheckoutBasketPriced = (state: RootState): boolean =>
  selectCheckoutBasketPricingOutcome(state) === 'priced';

/**
 * TEAM-14787: a validate has settled on the current checkout basket and none has priced it.
 * Distinct from "not priced yet": with no settled attempt (a reload restoring the basket from
 * sessionStorage) the answer is false, so the summary shows a spinner, not an error.
 */
export const selectCheckoutBasketPricingFailed = (state: RootState): boolean =>
  selectCheckoutBasketPricingOutcome(state) === 'failed';

/**
 * TEAM-14787 (AC3): the setup steps that run BEFORE validate. A failure in one of them left the
 * server basket without the diner's time or handoff choice, so its retry re-runs initialization.
 */
const PRE_VALIDATE_STEPS: readonly InitializationSteps[] = [
  InitializationSteps.SET_WANTED_AT,
  InitializationSteps.REMOVE_WANTED_AT,
  InitializationSteps.SET_CONVEYANCE,
];

/** TEAM-14787 (AC3): whether the current basket's pricing failure came from a pre-validate step. */
export const selectCheckoutBasketNeedsReinitialize = (state: RootState): boolean => {
  const step = state.app.orderStatus.basketPricing?.failedStep;
  return (
    selectCheckoutBasketPricingFailed(state) && Boolean(step && PRE_VALIDATE_STEPS.includes(step))
  );
};
